import React, { useEffect, useRef, useState } from 'react';
import { Badge, Button, DatePickerMobi, FilterIcon, Grid, Stack } from '@esgian/esgianui';
import moment from 'moment';
import { FleetCiiRatingAdvancedFilters } from '@components/Sections/EmissionsAnalytics/OperatorComparisonSection/OperatorFleetCiiRatingSection/OperatorFleetCiiRatingFilters/index';
import OperatorAdvancedMultiSelect from '@components/Inputs/OperatorAdvancedSelect';
import { useLookupOperatorsQuery, useSegment } from '@hooks';
import { useDispatch, useSelector } from 'react-redux';
import {
  getOprFleetCiiAdvFilterCount,
  getOprFleetCiiFilters
} from '@store/features/filters/EmissionAnalyticsPage/FleetCiiRatingSlice/FleetCiiRatingSlice';
import { updateOprFleetCiiFilters } from '@store/features/filters/EmissionAnalyticsPage/OperatorComparisonReducer/OperatorComparisonReducer';
import { OPERATOR_SELECT_TYPES } from '@constants';
import { syncFromZero, syncIntoZero } from '@helpers/syncOpVesSelection';
import { getOperatorType } from '@store/features';

function OperatorFleetCiiRatingFilters() {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const { segmentMinDate } = useSegment();
  const dispatch = useDispatch();
  const { data: lookupOperators } = useLookupOperatorsQuery();
  const fleetCiiRatingFilters = useSelector(getOprFleetCiiFilters);
  const filterCount = useSelector(getOprFleetCiiAdvFilterCount);
  const operatorType = useSelector(getOperatorType);
  const { startDate: year, selectType, selectedAssets } = fleetCiiRatingFilters;
  const lastManuallyDispatchedRef = useRef(false);
  const [cachedSelections, setCachedSelections] = useState({
    0: [],
    1: [],
    2: []
  });

  const [cachedSelectTypes, setCachedSelectTypes] = useState({
    0: OPERATOR_SELECT_TYPES.AUTO,
    1: OPERATOR_SELECT_TYPES.AUTO,
    2: OPERATOR_SELECT_TYPES.AUTO
  });

  const updateFilterValue = (payload) => {
    dispatch(updateOprFleetCiiFilters(payload));
  };

  const selectedOperators =
    operatorType === 0
      ? lookupOperators
      : lookupOperators?.filter((o) => o.operatorTypeId === operatorType);

  useEffect(() => {
    if (operatorType === 0) {
      syncFromZero(cachedSelections, setCachedSelections, setCachedSelectTypes);
    }
  }, [operatorType, cachedSelections]);

  useEffect(() => {
    if (operatorType === 1) {
      syncIntoZero(cachedSelections, setCachedSelections, setCachedSelectTypes, 1);
    }
  }, [operatorType, cachedSelections]);

  useEffect(() => {
    if (operatorType === 2) {
      syncIntoZero(cachedSelections, setCachedSelections, setCachedSelectTypes, 2);
    }
  }, [operatorType, cachedSelections]);

  useEffect(() => {
    const currentSelection = cachedSelections[operatorType] || [];
    const currentSelectType = cachedSelectTypes[operatorType] || OPERATOR_SELECT_TYPES.AUTO;

    const alreadyEqual =
      JSON.stringify(fleetCiiRatingFilters.selectedAssets) === JSON.stringify(currentSelection) &&
      fleetCiiRatingFilters.selectType === currentSelectType;

    if (alreadyEqual || lastManuallyDispatchedRef.current) {
      lastManuallyDispatchedRef.current = false; // Reset the flag after skipping
      return;
    }

    // Slight delay to ensure dispatch has landed
    setTimeout(() => {
      updateFilterValue({
        selectedAssets: [...currentSelection],
        selectType: currentSelectType
      });
    }, 0);
  }, [operatorType, cachedSelections, cachedSelectTypes]);

  return (
    <Grid item xs={12}>
      <Grid container spacing={2}>
        <Grid item lg={2.5} md={6} sm={10} className={'section-filter'}>
          <Stack spacing={1}>
            <DatePickerMobi
              sx={{
                inputProps: {
                  InputProps: {},
                  defaultValue: undefined,
                  value: year || '-',
                  placeholder: 'Period',
                  label: 'Period*'
                }
              }}
              selectedDate={year ? moment(`${year}`) : null}
              onClose={({ value }) => {
                if (moment(value).year() !== year) {
                  updateFilterValue({ startDate: moment(value).year() });
                }
              }}
              dateFormat={'YYYY'}
              minStartDate={segmentMinDate}
              disableFuture
              dateWheels={'YYYY'}
            />
          </Stack>
        </Grid>
        <Grid item>
          <OperatorAdvancedMultiSelect
            key={operatorType}
            selected={selectedAssets}
            autoEnabledText={'Displaying the top 10 operators given the selected year'}
            handleClose={(values) => {
              setCachedSelections((prev) => ({
                ...prev,
                [operatorType]: values.selectedOperators
              }));
              setCachedSelectTypes((prev) => ({
                ...prev,
                [operatorType]:
                  values.selectedOperators.length === 0
                    ? OPERATOR_SELECT_TYPES.AUTO
                    : OPERATOR_SELECT_TYPES.MANUAL
              }));
              dispatch(
                updateOprFleetCiiFilters({
                  selectedAssets: values.selectedOperators,
                  selectType:
                    values.selectedOperators.length === 0
                      ? OPERATOR_SELECT_TYPES.AUTO
                      : OPERATOR_SELECT_TYPES.MANUAL
                })
              );
              lastManuallyDispatchedRef.current = true;
            }}
            loading={!lookupOperators}
            operators={selectedOperators}
            selectType={selectType}
            label={'Operators*'}
          />
        </Grid>
        <Grid item alignSelf={'end'} sx={{ pb: '0.7em' }}>
          <Badge badgeContent={filterCount} color="error">
            <Button
              startIcon={<FilterIcon />}
              variant={'text'}
              onClick={() => setAdvancedOpen(true)}>
              Advanced Filter
            </Button>
          </Badge>
        </Grid>
        <FleetCiiRatingAdvancedFilters
          updateFilterValue={updateFilterValue}
          advancedOpen={advancedOpen}
          setAdvancedOpen={setAdvancedOpen}
        />
      </Grid>
    </Grid>
  );
}

OperatorFleetCiiRatingFilters.propTypes = {};

OperatorFleetCiiRatingFilters.defaultProps = {};

export default OperatorFleetCiiRatingFilters;
